const STRUCTURED_RENDER_TYPES = ['card', 'table', 'json', 'image', 'pdf', 'feedback_question'];
const ALLOWED_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const ALLOWED_PDF_MIME_TYPES = ['application/pdf'];
const MAX_INLINE_BASE64_LENGTH = 2 * 1024 * 1024;

const DOMAIN_TYPE_TO_RENDER_TYPE = {
    'fleet_status': 'card',
    'fleet_overview': 'card',
    'fleet_dashboard': 'card',
    'asset_status': 'card',
    'asset_overview': 'card',
    'condition_overview': 'card',
    'condition_alerts': 'card',
    'kpi_status': 'card',
    'maintenance_summary': 'card',
    'maintenance_overview': 'card',
    'maintenance_actions': 'card',
    'event_log': 'table',
    'event_timeline': 'table',
    'notification_list': 'table',
    'notification_summary': 'table',
    'report_list': 'table',
    'report_summary': 'table',
    'hierarchy_snapshot': 'json',
    'hierarchy_tree': 'json',
    'trend_snapshot': 'json',
    'poll_question': 'feedback_question',
    'confirmation_question': 'feedback_question'
};

const safeTrim = (value) => typeof value === 'string' ? value.trim() : '';
const safeLower = (value) => safeTrim(value).toLowerCase();

const toObjectOrNull = (value) => value && typeof value === 'object' && !Array.isArray(value) ? value : null;

const isLikelyBase64 = (value) => {
    const trimmed = safeTrim(value);
    if (!trimmed) {
        return false;
    }

    return /^[A-Za-z0-9+/]+={0,2}$/.test(trimmed) && trimmed.length % 4 === 0;
};

const parseDataUri = (value) => {
    const trimmed = safeTrim(value);
    const match = trimmed.match(/^data:([^;,]+);base64,(.+)$/i);
    if (!match) {
        return null;
    }

    return {
        mimeType: safeLower(match[1]),
        base64: safeTrim(match[2])
    };
};

const isValidMediaUrl = (value) => {
    const trimmed = safeTrim(value);
    if (!trimmed) {
        return false;
    }

    if (trimmed.startsWith('/')) {
        return true;
    }

    try {
        const parsedUrl = new URL(trimmed);
        return parsedUrl.protocol === 'https:' || parsedUrl.protocol === 'http:';
    } catch (error) {
        return false;
    }
};

const extractJsonCandidate = (messageContent) => {
    const trimmed = safeTrim(messageContent);
    if (!trimmed) {
        return '';
    }

    const fencedMatch = trimmed.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
    const withoutFence = fencedMatch ? safeTrim(fencedMatch[1]) : trimmed;

    if (withoutFence.startsWith('{') && withoutFence.endsWith('}')) {
        return withoutFence;
    }

    const firstBraceIndex = withoutFence.indexOf('{');
    const lastBraceIndex = withoutFence.lastIndexOf('}');
    if (firstBraceIndex < 0 || lastBraceIndex < 0 || lastBraceIndex <= firstBraceIndex) {
        return '';
    }

    return safeTrim(withoutFence.slice(firstBraceIndex, lastBraceIndex + 1));
};

const normalizeContentType = (rawType) => {
    const type = safeLower(rawType);
    if (STRUCTURED_RENDER_TYPES.includes(type)) {
        return type;
    }

    return DOMAIN_TYPE_TO_RENDER_TYPE[type] || '';
};

const normalizeCardStructuredData = (rawStructuredData) => {
    if (Array.isArray(rawStructuredData)) {
        return {
            cards: rawStructuredData.filter((entry) => entry && typeof entry === 'object')
        };
    }

    const structuredData = toObjectOrNull(rawStructuredData);
    if (!structuredData) {
        return null;
    }

    if (Array.isArray(structuredData.cards)) {
        return structuredData;
    }

    if (Array.isArray(structuredData.items)) {
        const cards = structuredData.items
            .filter((entry) => entry && typeof entry === 'object')
            .map((entry) => ({
                title: entry.title || entry.name || entry.label || '',
                description: entry.description || entry.summary || '',
                metadata: toObjectOrNull(entry.metadata) || {}
            }));

        return {
            ...structuredData,
            cards
        };
    }

    if (Array.isArray(structuredData.kpis)) {
        const cards = structuredData.kpis
            .filter((entry) => entry && typeof entry === 'object')
            .map((entry) => ({
                title: entry.title || entry.name || entry.label || entry.metric || '',
                description: entry.description || entry.summary || '',
                metadata: {
                    ...toObjectOrNull(entry.metadata),
                    value: entry.value,
                    unit: entry.unit,
                    status: entry.status,
                    trend: entry.trend
                }
            }));

        return {
            ...structuredData,
            cards
        };
    }

    if (toObjectOrNull(structuredData.summary)) {
        return {
            cards: [
                {
                    title: structuredData.title || structuredData.name || 'Summary',
                    description: structuredData.description || structuredData.summaryText || '',
                    metadata: structuredData.summary
                }
            ]
        };
    }

    return structuredData;
};

const inferColumnsFromRows = (rows) => {
    const firstRow = Array.isArray(rows)
        ? rows.find((row) => row && typeof row === 'object' && !Array.isArray(row))
        : null;

    return firstRow ? Object.keys(firstRow) : [];
};

const toTableRowsFromDomainArrays = (structuredData) => {
    const listKeys = ['events', 'notifications', 'reports', 'records', 'items'];
    const key = listKeys.find((listKey) => Array.isArray(structuredData?.[listKey]));
    if (!key) {
        return null;
    }

    return {
        key,
        rows: structuredData[key]
    };
};

const normalizeTableStructuredData = (rawStructuredData) => {
    if (Array.isArray(rawStructuredData)) {
        const inferredColumns = inferColumnsFromRows(rawStructuredData);

        return {
            columns: inferredColumns,
            rows: rawStructuredData
        };
    }

    const structuredData = toObjectOrNull(rawStructuredData);
    if (!structuredData) {
        return null;
    }

    const domainRows = toTableRowsFromDomainArrays(structuredData);
    if (domainRows && !Array.isArray(structuredData.rows) && !Array.isArray(structuredData.data)) {
        return {
            ...structuredData,
            rows: domainRows.rows,
            columns: Array.isArray(structuredData.columns) || Array.isArray(structuredData.headers)
                ? structuredData.columns || structuredData.headers
                : inferColumnsFromRows(domainRows.rows)
        };
    }

    if (!Array.isArray(structuredData.columns) && !Array.isArray(structuredData.headers) && Array.isArray(structuredData.rows)) {
        const inferredColumns = inferColumnsFromRows(structuredData.rows);
        if (inferredColumns.length > 0) {
            return {
                ...structuredData,
                columns: inferredColumns
            };
        }
    }

    if (!Array.isArray(structuredData.columns) && !Array.isArray(structuredData.headers) && Array.isArray(structuredData.data)) {
        const inferredColumns = inferColumnsFromRows(structuredData.data);
        if (inferredColumns.length > 0) {
            return {
                ...structuredData,
                columns: inferredColumns
            };
        }
    }

    return structuredData;
};

const normalizeMediaStructuredData = (contentType, rawStructuredData) => {
    const structuredData = toObjectOrNull(rawStructuredData);
    if (!structuredData) {
        return null;
    }

    const url = safeTrim(structuredData.url || structuredData.fileUrl || structuredData.src);
    const dataUri = parseDataUri(structuredData.base64 || structuredData.imageBase64 || structuredData.pdfBase64 || structuredData.dataUri);
    const directBase64 = dataUri ? '' : safeTrim(structuredData.base64 || structuredData.imageBase64 || structuredData.pdfBase64);
    const base64 = safeTrim(dataUri?.base64 || directBase64);
    const mimeType = safeLower(structuredData.mimeType || structuredData.mediaType || dataUri?.mimeType);
    const isImage = contentType === 'image';
    const allowedMimeTypes = isImage ? ALLOWED_IMAGE_MIME_TYPES : ALLOWED_PDF_MIME_TYPES;

    if (!isValidMediaUrl(url) && !isLikelyBase64(base64)) {
        return null;
    }

    if (mimeType && !allowedMimeTypes.includes(mimeType)) {
        return null;
    }

    const normalizedData = {
        ...structuredData,
        url: isValidMediaUrl(url) ? url : '',
        mimeType: mimeType || (isImage ? 'image/png' : 'application/pdf')
    };

    if (isLikelyBase64(base64) && base64.length <= MAX_INLINE_BASE64_LENGTH) {
        normalizedData.base64 = base64;
        if (isImage) {
            normalizedData.imageBase64 = base64;
        } else {
            normalizedData.pdfBase64 = base64;
        }
    } else if (isLikelyBase64(base64)) {
        delete normalizedData.base64;
        delete normalizedData.imageBase64;
        delete normalizedData.pdfBase64;
        delete normalizedData.dataUri;
        normalizedData.isBase64Truncated = true;
    }

    return normalizedData;
};

const normalizeFeedbackQuestionStructuredData = (rawStructuredData) => {
    const structuredData = toObjectOrNull(rawStructuredData);
    if (!structuredData) {
        return null;
    }

    const options = Array.isArray(structuredData.options)
        ? structuredData.options
            .filter((option) => option && typeof option === 'object')
            .map((option, index) => ({
                id: safeTrim(option.id || option.value || `option_${index + 1}`),
                label: safeTrim(option.label || option.title || option.text || option.value),
                description: safeTrim(option.description),
                disabled: Boolean(option.disabled)
            }))
            .filter((option) => option.id && option.label)
        : [];

    if (options.length === 0) {
        return null;
    }

    const selectionMode = safeLower(structuredData.selectionMode) === 'multiple' ? 'multiple' : 'single';
    const normalizedMaxSelections = Number(structuredData.maxSelections);
    const normalizedMinSelections = Number(structuredData.minSelections);

    return {
        questionId: safeTrim(structuredData.questionId || structuredData.id || structuredData.key),
        title: safeTrim(structuredData.title || structuredData.question || 'Feedback'),
        prompt: safeTrim(structuredData.prompt || structuredData.description),
        selectionMode,
        required: structuredData.required !== false,
        minSelections: Number.isFinite(normalizedMinSelections) && normalizedMinSelections > 0 ? normalizedMinSelections : 1,
        maxSelections: selectionMode === 'multiple' && Number.isFinite(normalizedMaxSelections) && normalizedMaxSelections > 0
            ? normalizedMaxSelections
            : selectionMode === 'multiple' ? options.length : 1,
        options,
        allowComment: Boolean(structuredData.allowComment),
        commentLabel: safeTrim(structuredData.commentLabel || 'Additional comments'),
        commentPlaceholder: safeTrim(structuredData.commentPlaceholder || 'Add details'),
        submitLabel: safeTrim(structuredData.submitLabel || 'Submit'),
        submitted: Boolean(structuredData.submitted),
        submittedAt: safeTrim(structuredData.submittedAt),
        submittedAnswer: toObjectOrNull(structuredData.submittedAnswer)
    };
};

const normalizeStructuredDataByType = (contentType, rawStructuredData) => {
    if (contentType === 'card') {
        return normalizeCardStructuredData(rawStructuredData);
    }

    if (contentType === 'table') {
        return normalizeTableStructuredData(rawStructuredData);
    }

    if (contentType === 'image' || contentType === 'pdf') {
        return normalizeMediaStructuredData(contentType, rawStructuredData);
    }

    if (contentType === 'feedback_question') {
        return normalizeFeedbackQuestionStructuredData(rawStructuredData);
    }

    if (contentType === 'json') {
        return rawStructuredData && typeof rawStructuredData === 'object' ? rawStructuredData : null;
    }

    return rawStructuredData && typeof rawStructuredData === 'object' ? rawStructuredData : null;
};

const QUERY_METADATA_KEYS = new Set(['query', 'model', 'top_k', 'score_threshold', 'context', 'retrieval_context']);

const isLikelyQueryMetadataObject = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }

    return Object.keys(value).some((key) => QUERY_METADATA_KEYS.has(key));
};

const parseMetadataCandidate = (candidate) => {
    try {
        return JSON.parse(candidate);
    } catch (error) {
        return null;
    }
};

const removeFirstMetadataBlock = (value) => {
    const matches = [...value.matchAll(/\{[^{}]*\}/g)].reverse();

    for (const match of matches) {
        const candidate = match[0];
        const startIndex = match.index;
        if (!candidate?.startsWith('{') || typeof startIndex !== 'number') {
            continue;
        }

        const parsedCandidate = parseMetadataCandidate(candidate);
        if (!isLikelyQueryMetadataObject(parsedCandidate)) {
            continue;
        }

        const beforeChar = value[startIndex - 1];
        const afterChar = value[startIndex + candidate.length];
        const spacing = beforeChar && !/\s/.test(beforeChar) && afterChar && !/\s/.test(afterChar) ? ' ' : '';

        return `${value.slice(0, startIndex)}${spacing}${value.slice(startIndex + candidate.length)}`;
    }

    return null;
};

export const stripQueryMetadataFromText = (value) => {
    if (typeof value !== 'string') {
        return value;
    }

    let cleanedValue = value;
    let nextValue = removeFirstMetadataBlock(cleanedValue);
    while (nextValue !== null) {
        cleanedValue = nextValue;
        nextValue = removeFirstMetadataBlock(cleanedValue);
    }

    return cleanedValue
        .replace(/[ \t]{2,}/g, ' ')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n[ \t]+/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
};

export const normalizeStructuredPayload = (parsedPayload) => {
    const payload = toObjectOrNull(parsedPayload);
    if (!payload) {
        return null;
    }

    const normalizedType = normalizeContentType(payload?.contentType || payload?.type);
    if (!normalizedType) {
        return null;
    }

    const rawStructuredData = payload?.structuredData
        ?? payload?.data
        ?? payload?.payload
        ?? payload?.content;
    const structuredData = normalizeStructuredDataByType(normalizedType, rawStructuredData);

    if (!structuredData || typeof structuredData !== 'object') {
        return null;
    }

    return {
        type: 'structured',
        contentType: normalizedType,
        structuredData,
        fallbackText: safeTrim(payload?.text || payload?.message || '')
    };
};

export const tryParseStructuredPayloadFromText = (messageContent) => {
    const trimmed = extractJsonCandidate(messageContent);
    if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) {
        return null;
    }

    try {
        const parsed = JSON.parse(trimmed);
        const normalized = normalizeStructuredPayload(parsed);
        if (!normalized) {
            return null;
        }

        return {
            ...normalized,
            fallbackText: normalized.fallbackText || trimmed
        };
    } catch (error) {
        return null;
    }
};

export const isLikelyStructuredPayloadStream = (messageContent) => {
    const trimmed = safeTrim(messageContent);
    if (!trimmed.startsWith('{')) {
        return false;
    }

    const containsStructuredSignature = trimmed.includes('"type"')
        || trimmed.includes('"contentType"')
        || trimmed.includes('"structuredData"')
        || trimmed.includes('"payload"');

    if (!containsStructuredSignature) {
        return false;
    }

    try {
        JSON.parse(trimmed);
        return false;
    } catch (error) {
        return true;
    }
};
